import { useMemo } from 'react';
import { useNavigation, useSearchParams } from 'react-router-dom';
import { Provider, ProviderAvailability } from '@types';
import { useFilters } from '../../components';
import EmptyState from '../EmptyState';
import Availability from './Availability';
import { filterAvailabilities, getSoonestProviderId } from './utils';
import { useApplicationStore } from '@data';

interface Props {
  availabilities: ProviderAvailability[];
  onSelectAvailability: (
    provider: Provider,
    time: string,
    position: number,
  ) => void;
}

export default function Availabilities({
  availabilities,
  onSelectAvailability,
}: Props): JSX.Element {
  const { hour, timeOfDay } = useFilters();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const { state } = useNavigation();
  const { soonestAvailabilityDate } = useApplicationStore.getState();
  const filteredAvailabilitiesWithTimes = useMemo(
    () =>
      filterAvailabilities({ availabilities, date: date!, hour, timeOfDay }),
    [availabilities, date, hour, timeOfDay],
  );

  const isNotLoading = state !== 'loading';
  const noAvailabilities = availabilities.length === 0;
  const noFilteredTimes = filteredAvailabilitiesWithTimes.every(
    (availability) => availability.times.length === 0,
  );
  const soonestProviderId = useMemo(
    () => getSoonestProviderId(filteredAvailabilitiesWithTimes || []),
    [filteredAvailabilitiesWithTimes],
  );

  if (isNotLoading && (noAvailabilities || noFilteredTimes)) {
    return <EmptyState />;
  }

  return (
    <div className="grid gap-4" data-testid="pw_schedule-providers">
      {filteredAvailabilitiesWithTimes.map((availability, idx) => (
        <Availability
          key={availability.provider.id}
          onSelectAvailability={onSelectAvailability}
          position={idx}
          times={availability.times}
          provider={availability.provider}
          lastEndedVisitDateTime={availability.lastEndedVisitDateTime}
          soonest={
            soonestAvailabilityDate === date &&
            soonestProviderId === availability.provider.id
          }
          timeSlotDate={availability.date}
          sameDate={date === availability.date}
        />
      ))}
    </div>
  );
}
